import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function Navbar() {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') return location.pathname === '/';
      return location.pathname.startsWith(path);
    },
    [location.pathname],
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-movie-2-line text-background-950 text-lg"></i>
            </div>
            <span className="text-xl font-heading font-bold text-foreground-50 whitespace-nowrap group-hover:text-primary-400 transition-colors">
              91电影视频
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`font-label text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/') && !isActive('/category') && !isActive('/about')
                  ? 'text-primary-400'
                  : 'text-foreground-300 hover:text-foreground-50'
              }`}
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`font-label text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'text-primary-400'
                    : 'text-foreground-300 hover:text-foreground-50'
                }`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`font-label text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/about')
                  ? 'text-primary-400'
                  : 'text-foreground-300 hover:text-foreground-50'
              }`}
            >
              关于我们
            </Link>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800/60 hover:bg-background-700 text-foreground-300 hover:text-foreground-50 transition-colors cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-base"></i>
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full bg-background-800/60 hover:bg-background-700 text-foreground-300 hover:text-foreground-50 transition-colors cursor-pointer"
              aria-label="菜单"
            >
              <i className={`text-base ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Search bar */}
        {isSearchOpen && (
          <div className="pb-4 px-1">
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索电影、电视剧、综艺..."
                  className="w-full pl-10 pr-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium rounded-lg whitespace-nowrap cursor-pointer transition-colors"
              >
                搜索
              </button>
            </form>
          </div>
        )}

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-background-800/50 pt-4">
            <div className="flex flex-col gap-2">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/') && !isActive('/category') && !isActive('/about')
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-foreground-300 hover:bg-background-800 hover:text-foreground-50'
                }`}
              >
                首页
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === `/category/${cat.slug}`
                      ? 'bg-primary-500/15 text-primary-400'
                      : 'text-foreground-300 hover:bg-background-800 hover:text-foreground-50'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/about')
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-foreground-300 hover:bg-background-800 hover:text-foreground-50'
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}